import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { CircleBtn, CircleBtnType } from "../../../../../../../react-core/Html/CircleBtn";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { Objects } from "../../../../../../../react-core/Utils/Objects";
import { BankAccountIdType } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountIdType";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { DrawdownPaymentDestinationAllocation } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownPaymentDestinationAllocation";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { BankAccountAction } from "../../PaymentsIn/BankAccount/BankAccountAction";
import { NewBankAccountInstruction } from "../../PaymentsIn/BankAccount/NewBankAccountInstruction";
import { PaymentType } from "../../Resource/Contents";
import { SectionHeader } from "../../SharedComponents";
import { incomePaymentDetailsLabels, incomePaymentDetailsMessages } from "./Resource/Content";

interface IIncomeBankDetailsProps extends IProcessComponentProps {
    data: DrawdownPaymentDestinationAllocation[];
    allBankAccounts?: BankAccount[];
    showPropotionOfIncome: boolean;
    showPropotionOfPcls?: boolean;
    OwnerHierarchyId?: string;
    bankAccountInstructions?: BankAccountInstruction[];
    onChangeBankAccountInstructions?: (changes: BankAccountInstruction[]) => void;
}

interface IIncomeBankDetailsState {
    splitAcrossTwoBankAccounts: boolean;

    addNewBankAccount: boolean;
}

export class IncomeBankDetails extends ProcessComponent<IIncomeBankDetailsProps, IIncomeBankDetailsState> {
    constructor(props: any) {
        super(props);
        this.state = {
            splitAcrossTwoBankAccounts: this.props.data?.length > 1,

            addNewBankAccount: false,
        };
    }

    isValid() {
        let flag = true;
        if (
            this.props.showPropotionOfIncome &&
            this.props.data.reduce((sum, cur) => (sum = sum + (cur.Allocation || 0)), 0) != 100
        ) {
            flag = false;
            this.props.showErrorMsgPopUp &&
                this.props.showErrorMsgPopUp([incomePaymentDetailsLabels.totalProportionOfIncomeNot100]);
        }
        if (
            this.props.showPropotionOfPcls &&
            this.props.data.reduce((sum, cur) => (sum = sum + (cur.PclsAllocation || 0)), 0) != 100
        ) {
            flag = false;
            this.props.showErrorMsgPopUp &&
                this.props.showErrorMsgPopUp([incomePaymentDetailsLabels.totalPortionOfPCLSNot100]);
        }
        return flag;
    }

    getBankAccountOption(): Map<number, string> {
        const { allBankAccounts, bankAccountInstructions } = this.props;
        const options = new Map();
        options.set(-1, incomePaymentDetailsLabels.wrapCash);
        if (!isNullOrEmpty(allBankAccounts)) {
            allBankAccounts.forEach((account) => {
                options.set(
                    account.BankAccountId,
                    `${account?.SortCode || ""} | ${account?.AccountNumber || ""} | ${account.HolderName || ""}`
                );
            });
        }
        if (!isNullOrEmpty(bankAccountInstructions)) {
            bankAccountInstructions.forEach((account) => {
                options.set(
                    account.Id,
                    `${account.BankCodes?.BankCode || ""} ${account.BankCodes?.ExtraCode || ""} | ${
                        account.AccountNumber || ""
                    } | ${account.AccountName || ""}`
                );
            });
        }

        return options;
    }

    private saveNewBankAccountInstruction(bankAccountInstruction: BankAccountInstruction) {
        const { onChangeBankAccountInstructions, bankAccountInstructions, OwnerHierarchyId } = this.props;

        if (
            (bankAccountInstructions || []).findIndex((x) => x.AccountNumber == bankAccountInstruction.AccountNumber) ==
            -1
        ) {
            bankAccountInstruction.OwnerHierarchyId = OwnerHierarchyId;
            BankAccountAction.saveBankInstruction(bankAccountInstruction, null, null, (savedBankAccountInstruction) => {
                const BankAccountInstructions = (bankAccountInstructions || []).concat(savedBankAccountInstruction);
                onChangeBankAccountInstructions(BankAccountInstructions);
                this.setState({ addNewBankAccount: false });
            });
        } else {
            return this.props.showErrorMsgPopUp(["Please enter a non-existing bank account"], true);
        }
    }

    initializedList(splitAcrossTwoBankAccounts: boolean) {
        const data = [this.getEmptyData(splitAcrossTwoBankAccounts)];
        if (splitAcrossTwoBankAccounts) {
            data.push(this.getEmptyData(splitAcrossTwoBankAccounts));
        }
        this.props.onChange(data);
    }

    getEmptyData(splitAcrossTwoBankAccounts: boolean): DrawdownPaymentDestinationAllocation {
        return {
            BankAccountId: null,
            BankAccountIdType: BankAccountIdType.BankAccount,
            Destination: IncomePaymentDestination.BankAccount,
            Allocation: splitAcrossTwoBankAccounts ? null : 100,
            PclsAllocation: splitAcrossTwoBankAccounts ? null : 100,
        };
    }

    getBankAccounts(bankAccountOptions: any, isPrimaryBankAccount: boolean) {
        const { data } = this.props;
        const keyList: number[] = Array.from(bankAccountOptions.keys());
        const index = isPrimaryBankAccount ? 1 : 0;
        return !this.state.splitAcrossTwoBankAccounts
            ? keyList
            : keyList.filter((key) => key !== data[index]?.BankAccountId);
    }

    onPortionIncomeChange(index: number, value: number) {
        const data = Objects.cloneObject(this.props.data);
        if (data[index]) {
            data[index].Allocation = value;
            this.props.onChange(data);
        }
    }

    onPortionPclsChange(index: number, value: number) {
        const data = Objects.cloneObject(this.props.data);
        if (data[index]) {
            data[index].PclsAllocation = value;
            this.props.onChange(data);
        }
    }

    onBankAccountIdChange(index: number, value: number) {
        const updatedData = Objects.cloneObject(this.props.data);
        if (updatedData[index]) {
            if (value == -1) {
                updatedData[index].Destination = IncomePaymentDestination.WrapCash;
                updatedData[index].BankAccountIdType = BankAccountIdType.NotSpecified;
            } else {
                updatedData[index].Destination = IncomePaymentDestination.BankAccount;
            }
            const isBankAccountInstrcution: boolean = (this.props.bankAccountInstructions || []).some(
                (ele) => ele.Id == value
            );
            if (isBankAccountInstrcution) {
                updatedData[index].BankAccountIdType = BankAccountIdType.BankAccountInstruction;
            }
            updatedData[index].BankAccountId = value;

            this.props.onChange(updatedData);
        }
    }

    public render(): JSX.Element {
        const { data, showPropotionOfIncome, onChangeBankAccountInstructions } = this.props;
        const { splitAcrossTwoBankAccounts, addNewBankAccount } = this.state;
        const bankAccountOptions = this.getBankAccountOption();
        if (isNullOrEmpty(this.props.data)) {
            this.initializedList(this.state.splitAcrossTwoBankAccounts);
        }
        const showAddBank = onChangeBankAccountInstructions && !addNewBankAccount;
        return (
            <>
                {(data || []).some((ele) => ele.BankAccountIdType == BankAccountIdType.BankAccountInstruction) && (
                    <Alert alertType={AlertType.Warning} message={incomePaymentDetailsLabels.bankAccountNotValidated} />
                )}
                {SectionHeader(incomePaymentDetailsLabels.bankDetailsHeader)}
                <div className="h4 mb-10" data-test="">
                    {incomePaymentDetailsLabels.selectBankAccount}
                </div>
                {bankAccountOptions.size > 1 && (
                    <div className="row">
                        <div className="col-12">
                            <CheckboxEditor
                                label={incomePaymentDetailsLabels.splitAcrossTwoBankAccounts}
                                value={splitAcrossTwoBankAccounts}
                                onChange={(v) => {
                                    this.initializedList(v);
                                    this.setState({ splitAcrossTwoBankAccounts: v });
                                }}
                                cssWrapper="square-checkbox"
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-6">
                        <SelectEditor
                            label={splitAcrossTwoBankAccounts ? incomePaymentDetailsLabels.bankAccount1 : ""}
                            options={this.getBankAccounts(bankAccountOptions, true)}
                            selected={!isNullOrEmpty(data) ? data[0]?.BankAccountId : null}
                            getText={(v) => {
                                return bankAccountOptions.get(v);
                            }}
                            onChange={(v) => {
                                this.onBankAccountIdChange(0, v);
                            }}
                            placeholder={"Please select"}
                            required={"Please select"}
                        />
                    </div>
                </div>
                {showAddBank && (
                    <div className="row">
                        <div className={`col-6 mb-20`}>
                            <CircleBtn
                                type={CircleBtnType.add}
                                title="Add new bank account"
                                onClick={() => {
                                    this.setState({ addNewBankAccount: true });
                                }}
                            />
                        </div>
                    </div>
                )}
                {addNewBankAccount && (
                    <Accordion
                        key={`New bank account`}
                        label={`New bank account`}
                        cssWrapper="accordion-simple mb-20"
                        isOpen={true}
                    >
                        <NewBankAccountInstruction
                            onChange={(instruction) => {
                                this.saveNewBankAccountInstruction(instruction);
                            }}
                            data={{
                                NominatedForWithdrawal: true,
                                Owner: BankAccountOwner.Individual,
                            }}
                            onRef={(ref) => this.appendChild(ref)}
                            payeeType={PaymentType.Client}
                            isReview={false}
                            withdrawalsOnly
                        />
                        <CircleBtn
                            cssWrapper="mt-20"
                            type={CircleBtnType.remove}
                            title="Remove this bank account"
                            onClick={() => this.setState({ addNewBankAccount: false })}
                        />
                    </Accordion>
                )}
                {this.state.splitAcrossTwoBankAccounts && (
                    <>
                        <div className="row">
                            <div className="col-md-4 col-12">
                                <PercentageTextBoxEditor
                                    label={incomePaymentDetailsLabels.portionOfPcls}
                                    value={data[0]?.PclsAllocation}
                                    atMost={{
                                        errorMessage: incomePaymentDetailsMessages.percentageOver100,
                                        value: 100,
                                    }}
                                    atLeast={{
                                        errorMessage: incomePaymentDetailsMessages.percentageUnder0,
                                        value: 0,
                                    }}
                                    onChange={(v) => {
                                        this.onPortionPclsChange(0, v);
                                    }}
                                />
                            </div>
                        </div>
                        {showPropotionOfIncome && (
                            <div className="row">
                                <div className="col-md-4 col-12">
                                    <PercentageTextBoxEditor
                                        label={incomePaymentDetailsLabels.proportionOfIncome}
                                        value={data[0]?.Allocation}
                                        atMost={{
                                            errorMessage: incomePaymentDetailsMessages.percentageOver100,
                                            value: 100,
                                        }}
                                        atLeast={{
                                            errorMessage: incomePaymentDetailsMessages.percentageUnder0,
                                            value: 0,
                                        }}
                                        onChange={(v) => {
                                            this.onPortionIncomeChange(0, v);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="row">
                            <div className="col-6">
                                <SelectEditor
                                    label={incomePaymentDetailsLabels.bankAccount2}
                                    options={this.getBankAccounts(bankAccountOptions, false)}
                                    selected={!isNullOrEmpty(data) ? data[1]?.BankAccountId : null}
                                    getText={(v) => {
                                        return bankAccountOptions.get(v);
                                    }}
                                    onChange={(v) => {
                                        this.onBankAccountIdChange(1, v);
                                    }}
                                    placeholder={"Please select"}
                                    required={"Please select"}
                                />
                            </div>
                        </div>
                        {this.props.showPropotionOfPcls && (
                            <div className="row">
                                <div className="col-md-4 col-12">
                                    <PercentageTextBoxEditor
                                        label={incomePaymentDetailsLabels.portionOfPcls}
                                        value={data[1]?.PclsAllocation}
                                        atMost={{
                                            errorMessage: incomePaymentDetailsMessages.percentageOver100,
                                            value: 100,
                                        }}
                                        atLeast={{
                                            errorMessage: incomePaymentDetailsMessages.percentageUnder0,
                                            value: 0,
                                        }}
                                        onChange={(v) => {
                                            this.onPortionPclsChange(1, v);
                                        }}
                                        validationRules={[
                                            {
                                                errorMessage:
                                                    incomePaymentDetailsMessages.totalProportionOfIncomeNot100,
                                                validator: (v) =>
                                                    !v ||
                                                    !data[0]?.PclsAllocation ||
                                                    v + data[0]?.PclsAllocation === 100,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        )}
                        {showPropotionOfIncome && (
                            <div className="row">
                                <div className="col-md-4 col-12">
                                    <PercentageTextBoxEditor
                                        label={incomePaymentDetailsLabels.proportionOfIncome}
                                        value={data[1]?.Allocation}
                                        atMost={{
                                            errorMessage: incomePaymentDetailsMessages.percentageOver100,
                                            value: 100,
                                        }}
                                        atLeast={{
                                            errorMessage: incomePaymentDetailsMessages.percentageUnder0,
                                            value: 0,
                                        }}
                                        onChange={(v) => {
                                            this.onPortionIncomeChange(1, v);
                                        }}
                                        validationRules={[
                                            {
                                                errorMessage:
                                                    incomePaymentDetailsMessages.totalProportionOfIncomeNot100,
                                                validator: (v) =>
                                                    !v || !data[0]?.Allocation || v + data[0]?.Allocation === 100,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }
}
export default IncomeBankDetails;
